import { useRef, useState } from 'react';

import { Attachment } from '@/models/api/Contact';
import { FileUtil } from '@/utils/FileUtil';

type AllowedFormatObject = {
    extension: string;
    type: string;
};

const ALLOWED_FILE_FORMATS = [
    { extension: 'jpg', type: 'image/jpeg' },
    { extension: 'jpeg', type: 'image/jpeg' },
    { extension: 'png', type: 'image/png' },
    { extension: 'gif', type: 'image/gif' },
    { extension: 'mp4', type: 'video/mp4' },
    { extension: 'avi', type: 'video/avi' },
    { extension: 'mov', type: 'video/quicktime' },
    { extension: 'wmv', type: 'video/x-ms-wmv' },
    { extension: 'pdf', type: 'application/pdf' }
] as const satisfies readonly AllowedFormatObject[];

const FILE_MAX_SIZE_MEGABYTES = 1;

type UploadErrorMessage = {
    code: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values?: Record<string, any>;
};

export function useFileUpload() {
    const [attachment, setAttachment] = useState<Attachment | null>(null);
    const [uploadErrorMessage, setUploadErrorMessage] = useState<UploadErrorMessage | null>();

    const fileRef = useRef<HTMLInputElement>(null);

    const fileInputAcceptAttribute = ALLOWED_FILE_FORMATS.map((item) => '.' + item.extension).join(
        ','
    );

    const clearUploadErrorMessage = () => setUploadErrorMessage(null);

    const handleFileRemove = () => {
        if (fileRef?.current) {
            fileRef.current.value = '';
            clearUploadErrorMessage();
            setAttachment(null);
        }
    };

    const handleFileUpload = async () => {
        const selectedFile = fileRef?.current?.files?.[0];

        if (!selectedFile) {
            setAttachment(null);
            clearUploadErrorMessage();
            return;
        }

        const { type, size, name } = selectedFile;

        if (!ALLOWED_FILE_FORMATS.some((item) => type === item.type)) {
            setFileExtensionErrorMessage();
            return;
        }

        if (size > FILE_MAX_SIZE_MEGABYTES * 1024 * 1024) {
            setFileSizeErrorMessage();
            return;
        }

        const base64 = await FileUtil.getBase64(selectedFile);

        if (base64) {
            setAttachment({ name, mimeType: type, data: base64 });
        }
        clearUploadErrorMessage();
    };

    const setFileExtensionErrorMessage = () => {
        setUploadErrorMessage({
            code: 'contact.error-file-extension',
            values: {
                fileFormats: ALLOWED_FILE_FORMATS.map((item) => item.extension).join(', ')
            }
        });
    };

    const setFileSizeErrorMessage = () => {
        setUploadErrorMessage({
            code: 'contact.error-file-size',
            values: {
                fileSize: FILE_MAX_SIZE_MEGABYTES
            }
        });
    };

    return {
        attachment,
        clearUploadErrorMessage,
        fileInputAcceptAttribute,
        fileRef,
        handleFileRemove,
        handleFileUpload,
        uploadErrorMessage
    };
}
